import { Slot } from "@radix-ui/react-slot";
import { ComponentProps, FC, useId } from "react";
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
} from "react-hook-form";

import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { HTMLProps } from "@/shared/types";

import { FormFieldContext, FormItemContext } from "./context";
import { useFormField } from "./hooks";

type FormItemProps = HTMLProps<"div">;
type FormLabelProps = ComponentProps<typeof Label>;
type FormControlProps = ComponentProps<typeof Slot>;
type FormDescriptionProps = HTMLProps<"p">;
type FormMessageProps = HTMLProps<"p">;

export const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: ControllerProps<TFieldValues, TName>,
) => (
  <FormFieldContext.Provider value={{ name: props.name }}>
    <Controller {...props} />
  </FormFieldContext.Provider>
);

export const FormItem: FC<FormItemProps> = ({ className, ...props }) => {
  const id = useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div
        data-slot="form-item"
        className={cn("grid gap-2", className)}
        {...props}
      />
    </FormItemContext.Provider>
  );
};

export const FormLabel: FC<FormLabelProps> = ({ className, ...props }) => {
  const { error, formItemId } = useFormField();

  return (
    <Label
      data-slot="form-label"
      data-error={!!error}
      className={cn(
        "flex data-[error=true]:text-destructive cursor-pointer",
        className,
      )}
      htmlFor={formItemId}
      {...props}
    />
  );
};

export const FormControl: FC<FormControlProps> = (props) => {
  const { error, formItemId, formDescriptionId, formMessageId } =
    useFormField();

  return (
    <Slot
      data-slot="form-control"
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  );
};

export const FormDescription: FC<FormDescriptionProps> = ({
  className,
  ...props
}) => {
  const { formDescriptionId } = useFormField();

  return (
    <p
      data-slot="form-description"
      id={formDescriptionId}
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
};

export const FormMessage: FC<FormMessageProps> = ({ className, ...props }) => {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message) : props.children;

  if (!body) {
    return null;
  }

  return (
    <p
      data-slot="form-message"
      id={formMessageId}
      className={cn(
        error
          ? "text-destructive text-sm font-medium"
          : "text-muted-foreground text-sm",
        className,
      )}
      {...props}
    >
      {body}
    </p>
  );
};
